import { MetricType } from '@hub/libs/constants';
import {
  RangeDirectionChange,
  useBearingsTableStore,
} from '@hub/bearing-hub/BearingHubPage/BearingsTable/useBearingsTableStore';
import { Icon, IconID } from '@hub/common/Icon/Icon';
import type { IbhDimensions, ShapeDescriptor } from '@hub/bearing-hub/common/types';
import { getDimensionLabel } from '@hub/bearing-hub/common/utils';
import type { IbhPartsResponseDto } from '@igus/iglidur-bearing-hub-api/src/data-contracts';

export interface BearingTableDimensionProps {
  data: IbhPartsResponseDto;
  selectedShape: ShapeDescriptor;
  locale: string;
  showHeader?: boolean;
  usedDimensions?: IbhDimensions;
  metricType: MetricType;
}

export const BearingTableDimensions = ({
  showHeader = false,
  selectedShape,
  data,
  locale,
  usedDimensions,
  metricType,
}: BearingTableDimensionProps) => {
  const {
    shapeDimensionsHeadersKeysToDisplay,
    presentDimensionsDataFrom,
    presentDimensionsDataTo,
    allShapeDimensionsHeadersKeys,
    updateChangeRangeDirection,
  } = useBearingsTableStore();
  return (
    <div className="flex flex-col overflow-auto relative w-full">
      {showHeader && (
        <div className="relative overflow-hidden">
          <div
            className={`absolute left-[-5px] top-[1px] bottom-0.5 cursor-pointer ${
              presentDimensionsDataFrom === 0 ? 'invisible' : 'visible'
            }`}
            onClick={() => updateChangeRangeDirection(RangeDirectionChange.Back)}
          >
            <Icon size={8} id={IconID.CHEVRON_LEFT} />
          </div>
          <div className="flex flex-row leading-[2.4] items-center w-full border-b-[1px] border-solid border-[#AFAFAF]">
            {shapeDimensionsHeadersKeysToDisplay.map(definition => (
              <div
                key={getDimensionLabel(definition, selectedShape.shapeKey)}
                className="text-xs font-medium grow basis-0 text-center xs:min-w-[50px]"
              >
                {getDimensionLabel(definition, selectedShape.shapeKey)}
              </div>
            ))}
          </div>
          <div
            className={`absolute bottom-0.5 cursor-pointer right-[-5px] top-[1px] ${
              presentDimensionsDataTo >= allShapeDimensionsHeadersKeys.length ? 'invisible' : 'visible'
            }`}
            onClick={() => updateChangeRangeDirection(RangeDirectionChange.Forward)}
          >
            <Icon size={8} id={IconID.CHEVRON_RIGHT} />
          </div>
        </div>
      )}
      <div
        className={`flex flex-row leading-[2.4] items-center w-full justify-center${
          showHeader ? 'border-b-[1px] border-solid border-[#AFAFAF]' : ''
        }`}
      >
        {shapeDimensionsHeadersKeysToDisplay.map(label => {
          let doMark = false;
          if (usedDimensions) {
            const usedDimensionValue = usedDimensions[label as keyof IbhDimensions];
            const newDimensionValue = data.dimensions[label as keyof IbhDimensions];

            if (usedDimensionValue === newDimensionValue) {
              doMark = true;
            }
          }

          return (
            <div
              key={label}
              className="whitespace-nowrap text-xs grow text-center lg:min-w-0 min-w-[50px] w-full"
              data-testid={`dimension-${label}`}
            >
              <span className={doMark ? 'text-[#1C8B41]' : ''}>
                {new Intl.NumberFormat(locale, {
                  maximumFractionDigits: metricType === MetricType.INCH ? 4 : 2,
                  minimumFractionDigits: metricType === MetricType.INCH ? 4 : 2,
                }).format(data.dimensions[label as keyof IbhDimensions])}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
